import { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WEEKDAY_SLOTS = ["6:00 pm", "7:00 pm"];
const FLEXIBLE_SLOTS = ["Morning", "Afternoon", "Evening"];
const AVAILABILITY = {
  Monday: WEEKDAY_SLOTS,
  Tuesday: WEEKDAY_SLOTS,
  Wednesday: WEEKDAY_SLOTS,
  Thursday: WEEKDAY_SLOTS,
  Friday: FLEXIBLE_SLOTS,
  Saturday: FLEXIBLE_SLOTS,
};
const DAYS = Object.keys(AVAILABILITY);

const scrollToForm = () => {
  document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
};

const RESOURCE_LINKS = [
  {
    label: "Finding Yourself After The Storm",
    href: "https://after-the-storm-2.preview.emergentagent.com/program",
    testid: "program",
  },
  {
    label: "Free Two-Minute Check-In",
    href: "https://after-the-storm-2.preview.emergentagent.com/check-in",
    testid: "checkin",
  },
  {
    label: "Free Intro",
    href: "https://after-the-storm-2.preview.emergentagent.com/free-intro",
    testid: "intro",
  },
];

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const goHome = (e) => {
    e.preventDefault();
    setMenuOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 300);
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };
  const goPage = (path) => (e) => {
    e.preventDefault();
    setMenuOpen(false);
    if (path === "/") return goHome(e);
    if (location.pathname !== path) {
      navigate(path);
      window.scrollTo(0, 0);
    }
  };
  const goTo = (id) => (e) => {
    e.preventDefault();
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 500);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };
  return (
  <header className="w-full bg-[#FAF9F5]/95 border-b border-[#E3ECE8] sticky top-0 z-30 backdrop-blur-sm">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between gap-3">
      <a href="/" onClick={goHome} data-testid="header-logo-link" className="flex items-center shrink-0">
        <img
          src="/safe-harbor-logo.png?v=2"
          alt="Safe Harbor Supports You — lighthouse and sailboat logo"
          data-testid="header-logo"
          className="h-11 sm:h-14 w-auto object-contain mix-blend-multiply"
        />
      </a>
      <nav className="hidden md:flex items-center gap-6 text-xs font-bold uppercase tracking-wider text-[#355B63]">
        <a data-testid="nav-home-link" href="/" onClick={goHome} className="hover:text-[#D4AF37] transition-colors">Home</a>
        <a data-testid="nav-counselling-link" href="/counselling" onClick={goPage("/counselling")} className="hover:text-[#D4AF37] transition-colors">Counselling</a>
        <a data-testid="nav-about-link" href="/about" onClick={goPage("/about")} className="hover:text-[#D4AF37] transition-colors">About Jo</a>
        <a data-testid="nav-faq-link" href="/faq" onClick={goPage("/faq")} className="hover:text-[#D4AF37] transition-colors">FAQ</a>
        <div className="relative" onMouseLeave={() => setResourcesOpen(false)}>
          <button
            type="button"
            data-testid="nav-resources-btn"
            onClick={() => setResourcesOpen((o) => !o)}
            aria-expanded={resourcesOpen}
            className="flex items-center gap-1 uppercase tracking-wider hover:text-[#D4AF37] transition-colors"
          >
            Resources
            <svg className={`w-3 h-3 transition-transform duration-200 ${resourcesOpen ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {resourcesOpen && (
            <div data-testid="resources-dropdown" className="absolute right-0 top-full mt-3 w-64 bg-white rounded-xl border border-[#D3E8E4] shadow-lg py-2 z-40">
              {RESOURCE_LINKS.map((r) => (
                <a
                  key={r.testid}
                  data-testid={`resources-${r.testid}-link`}
                  href={r.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setResourcesOpen(false)}
                  className="flex items-center justify-between gap-2 px-4 py-2.5 text-[#355B63] hover:bg-[#F4F9F8] hover:text-[#1B365D] transition-colors normal-case tracking-normal text-sm font-semibold"
                >
                  {r.label}
                  <svg className="w-3.5 h-3.5 shrink-0 text-[#3A7D81]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              ))}
            </div>
          )}
        </div>
      </nav>
      <button
        type="button"
        onClick={() => setMenuOpen((o) => !o)}
        data-testid="mobile-menu-btn"
        aria-label="Open menu"
        className="md:hidden ml-auto mr-1 w-9 h-9 flex items-center justify-center rounded-lg border border-[#D3E8E4] bg-white/70 text-[#1B365D] hover:bg-[#E4F0EE] transition-colors"
      >
        {menuOpen ? (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 7h16M4 12h16M4 17h16" />
          </svg>
        )}
      </button>
      <button
        type="button"
        onClick={goTo("contact")}
        data-testid="header-book-session-btn"
        className="shrink-0 whitespace-nowrap px-3.5 sm:px-5 py-2 sm:py-2.5 rounded-lg text-[11px] sm:text-xs font-bold uppercase tracking-wider bg-[#E5C158] text-[#1B365D] hover:bg-[#D4AF37] transition-colors duration-150 shadow-sm"
      >
        Book Session
      </button>
    </div>
    {menuOpen && (
      <div data-testid="mobile-menu" className="md:hidden absolute top-full left-0 right-0 bg-[#FAF9F5] border-b border-[#E3ECE8] shadow-md">
        <nav className="px-6 py-2 flex flex-col text-xs font-bold uppercase tracking-wider text-[#355B63]">
          <a data-testid="mobile-nav-home" href="/" onClick={goHome} className="py-3.5 border-b border-[#E3ECE8] hover:text-[#D4AF37] transition-colors">Home</a>
          <a data-testid="mobile-nav-counselling" href="/counselling" onClick={goPage("/counselling")} className="py-3.5 border-b border-[#E3ECE8] hover:text-[#D4AF37] transition-colors">Counselling</a>
          <a data-testid="mobile-nav-about" href="/about" onClick={goPage("/about")} className="py-3.5 border-b border-[#E3ECE8] hover:text-[#D4AF37] transition-colors">About Jo</a>
          <a data-testid="mobile-nav-faq" href="/faq" onClick={goPage("/faq")} className="py-3.5 border-b border-[#E3ECE8] hover:text-[#D4AF37] transition-colors">FAQ</a>
          <p data-testid="mobile-resources-label" className="pt-4 pb-1 text-[10px] font-bold uppercase tracking-[0.15em] text-[#3A7D81]">Resources</p>
          {RESOURCE_LINKS.map((r) => (
            <a
              key={r.testid}
              data-testid={`mobile-resources-${r.testid}`}
              href={r.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMenuOpen(false)}
              className="py-3 pl-3 flex items-center justify-between gap-2 border-b border-[#E3ECE8] last:border-b-0 hover:text-[#D4AF37] transition-colors"
            >
              {r.label}
              <svg className="w-3.5 h-3.5 shrink-0 text-[#3A7D81]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          ))}
        </nav>
      </div>
    )}
  </header>
  );
};

const Footer = () => (
  <footer className="bg-[#1B365D] text-white py-6 text-center text-xs">
    <p className="font-bold tracking-wider">Safe Harbor Supports You</p>
    <p className="text-[#A4C4C2] text-[11px] mt-1">Counselling • Recovery • Rebuilding</p>
  </footer>
);

const Hero = () => (
  <section id="top" className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-[#FAF9F5] via-[#F2F8F7] to-[#FAF6EE] relative">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 hero-enter">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
        {/* Left column: copy + gold CTA */}
        <div className="lg:col-span-7 space-y-6">
          <div
            data-testid="hero-badge"
            className="inline-flex items-center gap-2 px-3.5 py-1 rounded-md bg-[#E4F0EE] border border-[#C6E2DD] text-xs font-semibold text-[#2D5A60]"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]"></span>
            <span>A safer you is possible</span>
          </div>

          <h1 data-testid="hero-headline" className="hero-display text-3xl sm:text-5xl font-black text-[#1B365D] leading-[1.1]">
            You Survived What Happened. <br />
            <span className="text-[#2D5A60] font-bold">Begin Moving Forward Gently.</span>
          </h1>

          <p data-testid="hero-tagline" className="text-xs font-bold uppercase tracking-[0.2em] text-[#3A7D81]">
            Counselling • Recovery • Rebuilding
          </p>

          <div data-testid="hero-copy" className="text-base sm:text-lg text-[#355B63] space-y-4 leading-relaxed font-normal">
            <p>
              Leaving a controlling, abusive or emotionally harmful relationship doesn’t always bring the relief you expected.
            </p>
            <p className="text-[#1E373B] font-medium bg-[#EBF5F3]/70 p-4 rounded-xl border-l-4 border-[#3A7D81]">
              Safe Harbor offers a calm, supportive counselling space to help you understand what you’ve been through,
              reconnect with yourself and begin moving forward — at your own pace.
            </p>
          </div>

          <div className="pt-2 space-y-3">
            <button
              type="button"
              onClick={scrollToForm}
              data-testid="hero-book-session-btn"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl text-sm sm:text-base font-extrabold uppercase tracking-wider bg-[#E5C158] hover:bg-[#D4AF37] text-[#1B365D] shadow-md hover:shadow-lg transition-shadow duration-150 transform hover:-translate-y-0.5 border border-[#F1DC9B]"
            >
              <svg className="w-5 h-5 text-[#1B365D]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V5a2 2 0 012-2h4a2 2 0 012 2v2m-9 4h10M5 7h14a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9a2 2 0 012-2z" />
              </svg>
              Book a Counselling Session
            </button>
            <p data-testid="hero-reassurance" className="text-xs sm:text-sm text-[#4E757D] italic">
              “You don’t need to have it all figured out. You can start exactly where you are.”
            </p>
          </div>
        </div>

        {/* Right column: care card with wave motif */}
        <div className="lg:col-span-5" id="care">
          <div
            data-testid="care-card"
            className="rounded-3xl p-6 bg-gradient-to-b from-[#E7F3F1] via-[#FBF9F2] to-[#F3EEE0] border border-[#D3E8E4] shadow-md relative overflow-hidden"
          >
            <svg className="absolute -bottom-4 right-0 w-full h-32 opacity-35 text-[#3A7D81]" viewBox="0 0 400 150" fill="none">
              <path d="M0 60 C120 120 280 20 400 80 L400 150 L0 150 Z" fill="currentColor" />
              <path d="M0 90 C150 40 260 140 400 100" stroke="#D4AF37" strokeWidth="2" />
            </svg>

            <div className="relative z-10 space-y-5">
              <div className="flex items-center justify-between pb-3 border-b border-[#D3E8E4]">
                <span className="text-xs font-bold text-[#1B365D] uppercase tracking-wider">Counselling Care</span>
                <span className="text-[11px] font-semibold text-[#3A7D81]">Jo Hobbs, Counsellor</span>
              </div>

              <div className="space-y-3">
                <div data-testid="care-paced-gentle" className="p-3.5 bg-white/80 rounded-xl border border-[#DCEBE8]">
                  <h2 className="text-xs font-bold text-[#1B365D] uppercase tracking-wide">Paced &amp; Gentle</h2>
                  <p className="text-xs text-[#4E757D] mt-0.5">
                    Sessions move at your pace. There’s no pressure to share more than you’re ready for.
                  </p>
                </div>
                <div data-testid="care-confidential-space" className="p-3.5 bg-white/80 rounded-xl border border-[#DCEBE8]">
                  <h2 className="text-xs font-bold text-[#1B365D] uppercase tracking-wide">Confidential Space</h2>
                  <p className="text-xs text-[#4E757D] mt-0.5">
                    A private, supportive place to talk — with online counselling available wherever you are in Australia.
                  </p>
                </div>
              </div>

              <div className="pt-2">
                <button
                  type="button"
                  onClick={scrollToForm}
                  data-testid="card-request-appointment-btn"
                  className="block w-full text-center py-2.5 px-4 rounded-lg bg-[#1B365D] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#2D5A60] transition-colors"
                >
                  Request an Appointment Now
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const AppointmentForm = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", day: "", time: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const timeOptions = form.day ? AVAILABILITY[form.day] : [];

  const update = (field) => (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, [field]: value, ...(field === "day" ? { time: "" } : {}) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setErrorMsg("");
    try {
      await axios.post(`${API}/appointment-requests`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        day: form.day,
        time: form.time,
        message: form.message || null,
      });
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMsg(err.response?.data?.detail || "Something went wrong. Please try again, or call Jo on 0432 300 492.");
    }
  };

  const inputCls =
    "w-full px-3.5 py-2.5 rounded-lg border border-[#D3E8E4] bg-[#FAF9F5] text-sm text-[#1B365D] focus:outline-none focus:border-[#E5C158] transition-colors";
  const labelCls = "block text-xs font-bold uppercase tracking-wider text-[#2D5A60] mb-1";

  if (status === "success") {
    return (
      <div data-testid="form-success-message" className="bg-white rounded-2xl p-8 border border-[#D3E8E4] shadow-sm text-center space-y-3">
        <div className="w-12 h-12 mx-auto rounded-full bg-[#E4F0EE] flex items-center justify-center">
          <svg className="w-6 h-6 text-[#2D5A60]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-xl sm:text-2xl font-bold text-[#1B365D]">Thank you — your request has been sent.</h2>
        <p className="text-sm text-[#4E757D] leading-relaxed">
          Jo will be in touch personally to arrange your {form.day} appointment. You’ve taken a gentle first step.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-6 sm:p-8 border border-[#D3E8E4] shadow-sm">
      <h2 className="text-xl sm:text-2xl font-bold text-[#1B365D] text-center mb-1">Request an Appointment</h2>
      <p className="text-xs sm:text-sm text-[#4E757D] text-center mb-2">
        Reach out directly to Jo Hobbs. You can start exactly where you are.
      </p>
      <p data-testid="form-availability-note" className="text-[11px] text-[#3A7D81] text-center mb-6 font-semibold">
        Sessions: Mon–Thu 6 pm &amp; 7 pm • Friday &amp; Saturday by appointment • Closed Sundays
      </p>

      <form data-testid="appointment-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="appt-name" className={labelCls}>Name *</label>
          <input id="appt-name" data-testid="form-name-input" type="text" required placeholder="Your full name"
            value={form.name} onChange={update("name")} className={inputCls} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="appt-email" className={labelCls}>Email *</label>
            <input id="appt-email" data-testid="form-email-input" type="email" required placeholder="you@example.com"
              value={form.email} onChange={update("email")} className={inputCls} />
          </div>
          <div>
            <label htmlFor="appt-phone" className={labelCls}>Phone (Optional)</label>
            <input id="appt-phone" data-testid="form-phone-input" type="tel" placeholder="0400 000 000"
              value={form.phone} onChange={update("phone")} className={inputCls} />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="appt-day" className={labelCls}>Preferred Day *</label>
            <select id="appt-day" data-testid="form-day-select" required value={form.day} onChange={update("day")} className={inputCls}>
              <option value="" disabled>Choose a day</option>
              {DAYS.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="appt-time" className={labelCls}>Preferred Time *</label>
            <select id="appt-time" data-testid="form-time-select" required value={form.time} onChange={update("time")}
              disabled={!form.day} className={inputCls}>
              <option value="" disabled>{form.day ? "Choose a time" : "Choose a day first"}</option>
              {timeOptions.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="appt-message" className={labelCls}>Brief Message (Optional)</label>
          <textarea id="appt-message" data-testid="form-message-input" rows="3" placeholder="Any questions or notes..."
            value={form.message} onChange={update("message")} className={inputCls}></textarea>
        </div>

        {status === "error" && (
          <p data-testid="form-error-message" className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {errorMsg}
          </p>
        )}

        <button type="submit" data-testid="form-submit-btn" disabled={status === "sending"}
          className="w-full py-3.5 rounded-lg bg-[#E5C158] hover:bg-[#D4AF37] disabled:opacity-60 text-[#1B365D] font-bold text-xs sm:text-sm uppercase tracking-wider transition-colors shadow-sm">
          {status === "sending" ? "Sending…" : "Send Appointment Request"}
        </button>
      </form>
    </div>
  );
};

const AfterLeaving = () => {
  const questions = [
    "Why do I still miss them?",
    "Why do I question whether it really was that bad?",
    "Why do I feel guilty for choosing myself?",
    "Why am I still anxious when the relationship is over?",
    "Why don’t I recognise myself anymore?",
  ];
  return (
    <section id="after-leaving" data-testid="after-leaving-section" className="py-16 sm:py-20 bg-[#E7F2EF] border-t border-[#DDECE9] relative overflow-hidden">
      <svg className="absolute -top-6 left-0 w-full h-24 opacity-25 text-[#75B2A9]" viewBox="0 0 400 100" fill="none" preserveAspectRatio="none">
        <path d="M0 50 C120 90 280 10 400 60" stroke="currentColor" strokeWidth="2" />
        <path d="M0 70 C150 30 260 100 400 55" stroke="#D4AF37" strokeWidth="1.5" />
      </svg>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 relative z-10">
        <h2 data-testid="after-leaving-heading" className="hero-display text-2xl sm:text-4xl font-black text-[#1B365D] leading-tight text-center">
          I Left. Why Don’t I Feel Better?
        </h2>
        <p className="text-base sm:text-lg text-[#355B63] text-center mt-5 leading-relaxed">
          You thought leaving would be the hardest part.<br />But maybe now you’re wondering:
        </p>

        <div data-testid="after-leaving-questions" className="mt-8 space-y-3">
          {questions.map((q, i) => (
            <div key={i} className="flex items-start gap-3 bg-white/80 rounded-xl border border-[#D3E8E4] px-4 py-3.5 shadow-sm">
              <span className="mt-1.5 w-2 h-2 rounded-full bg-[#D4AF37] shrink-0"></span>
              <p className="text-sm sm:text-base text-[#1E373B] font-medium">{q}</p>
            </div>
          ))}
        </div>

        <p className="text-sm sm:text-base text-[#4E757D] text-center mt-8 leading-relaxed">
          These feelings can be difficult to understand after spending a long time questioning yourself,
          keeping the peace, walking on eggshells or putting someone else’s needs ahead of your own.
        </p>

        <div data-testid="after-leaving-closing" className="mt-8 bg-[#FAF6EE] border-l-4 border-[#E5C158] rounded-xl p-5 sm:p-6 text-center space-y-2">
          <p className="text-base sm:text-lg text-[#1B365D] font-bold">Leaving may end the relationship.</p>
          <p className="text-base sm:text-lg text-[#2D5A60] font-bold">Recovery is what comes next.</p>
          <p className="text-sm sm:text-base text-[#355B63]">You don’t have to work that part out alone.</p>
        </div>

        <div className="mt-8 text-center">
          <button
            type="button"
            onClick={scrollToForm}
            data-testid="talk-with-jo-btn"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl text-sm sm:text-base font-extrabold uppercase tracking-wider bg-[#E5C158] hover:bg-[#D4AF37] text-[#1B365D] shadow-md hover:shadow-lg transition-shadow duration-150 transform hover:-translate-y-0.5 border border-[#F1DC9B]"
          >
            Talk With Jo
          </button>
        </div>
      </div>
    </section>
  );
};

const Home = () => (
  <div className="min-h-screen bg-[#FAF9F5] text-[#1E373B] antialiased selection:bg-[#E5C158]/30">
    <Header />
    <main>
      <Hero />

      <section id="contact" className="py-16 bg-[#F4F9F8] border-t border-[#DDECE9]">
        <div className="max-w-xl mx-auto px-4 sm:px-6">
          <AppointmentForm />
        </div>
      </section>

      <AfterLeaving />

      <section id="location" className="py-10 bg-[#FAF9F5] border-t border-[#E3ECE8]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div data-testid="location-strip" className="p-4 rounded-xl bg-[#F0F7F6] border border-[#DCEBE8] inline-block">
            <p className="text-xs font-bold text-[#1B365D]">
              Safe Harbor Supports You • Based in the Bundaberg Region, QLD
            </p>
            <p className="text-[11px] text-[#4E757D] mt-1">
              Online counselling available Australia-wide.
            </p>
          </div>
        </div>
      </section>
    </main>

    <Footer />
  </div>
);

const AboutPage = () => {
  const navigate = useNavigate();
  const talkWithJo = () => {
    navigate("/");
    setTimeout(() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }), 500);
  };

  const approachCards = [
    {
      title: "No judgement",
      text: "You can talk honestly without having to defend or explain your feelings.",
      testid: "approach-no-judgement",
    },
    {
      title: "Your pace",
      text: "You decide what you’re ready to talk about. There is no pressure to disclose more than you want to.",
      testid: "approach-your-pace",
    },
    {
      title: "Real conversation",
      text: "Counselling doesn’t need to feel cold or clinical. I want our sessions to feel like a genuine conversation in a safe, supportive space.",
      testid: "approach-real-conversation",
    },
  ];

  const qualifications = ["Diploma of Counselling", "Blue Card", "NDIS Worker Screening Clearance"];

  return (
    <div className="min-h-screen bg-[#FAF9F5] text-[#1E373B] antialiased selection:bg-[#E5C158]/30">
      <Header />
      <main>
        {/* Intro */}
        <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-[#FAF9F5] via-[#F2F8F7] to-[#FAF6EE]">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 hero-enter">
            <div
              data-testid="about-badge"
              className="inline-flex items-center gap-2 px-3.5 py-1 rounded-md bg-[#E4F0EE] border border-[#C6E2DD] text-xs font-semibold text-[#2D5A60]"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]"></span>
              <span>A safer you is possible</span>
            </div>
            <h1 data-testid="about-heading" className="hero-display text-3xl sm:text-5xl font-black text-[#1B365D] leading-[1.1] mt-5">
              About Jo
            </h1>
            <div data-testid="about-intro" className="text-base sm:text-lg text-[#355B63] space-y-4 leading-relaxed mt-6">
              <p>
                Hi, I’m <span className="font-semibold text-[#1B365D]">Jo Hobbs</span>, founder and counsellor at Safe Harbor Supports You.
              </p>
              <p>
                I created Safe Harbor because I believe people deserve somewhere they can speak openly about what they’ve
                experienced without feeling judged, rushed or told how they should feel.
              </p>
              <p className="text-[#1E373B] font-medium bg-[#EBF5F3]/70 p-4 rounded-xl border-l-4 border-[#3A7D81]">
                My approach to counselling is warm, straightforward and person-centred. You don’t need to arrive with the
                right words, have everything figured out, or tell your whole story in the first session. We start wherever you are.
              </p>
            </div>
          </div>
        </section>

        {/* Why Safe Harbor */}
        <section className="py-14 sm:py-16 bg-[#F4F9F8] border-t border-[#DDECE9]">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 data-testid="why-safe-harbor-heading" className="hero-display text-2xl sm:text-3xl font-black text-[#1B365D]">
              Why Safe Harbor
            </h2>
            <div data-testid="why-safe-harbor-copy" className="text-base sm:text-lg text-[#355B63] space-y-4 leading-relaxed mt-5">
              <p>
                Safe Harbor has a particular focus on supporting people recovering after controlling, abusive or emotionally
                harmful relationships.
              </p>
              <p>
                Leaving can be only the beginning. Afterwards can come confusion, grief, self-doubt, anxiety, loneliness and
                the difficult task of working out who you are again.
              </p>
              <p>
                Counselling can give you a private space to make sense of what happened and begin rebuilding at your own pace.
              </p>
            </div>
          </div>
        </section>

        {/* What counselling with me is like */}
        <section className="py-14 sm:py-16 bg-[#E7F2EF] border-t border-[#DDECE9] relative overflow-hidden">
          <svg className="absolute -bottom-6 right-0 w-full h-28 opacity-25 text-[#3A7D81]" viewBox="0 0 400 150" fill="none" preserveAspectRatio="none">
            <path d="M0 60 C120 120 280 20 400 80 L400 150 L0 150 Z" fill="currentColor" />
            <path d="M0 90 C150 40 260 140 400 100" stroke="#D4AF37" strokeWidth="2" />
          </svg>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <h2 data-testid="approach-heading" className="hero-display text-2xl sm:text-3xl font-black text-[#1B365D]">
              What counselling with me is like
            </h2>
            <div data-testid="approach-cards" className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 mt-8">
              {approachCards.map((c) => (
                <div key={c.testid} data-testid={c.testid} className="bg-white/85 rounded-2xl border border-[#D3E8E4] p-5 sm:p-6 shadow-sm">
                  <span className="inline-block w-8 h-1 rounded-full bg-[#E5C158] mb-3"></span>
                  <h3 className="text-sm font-bold text-[#1B365D] uppercase tracking-wider">{c.title}</h3>
                  <p className="text-sm text-[#4E757D] mt-2 leading-relaxed">{c.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Qualifications */}
        <section className="py-14 sm:py-16 bg-[#FAF9F5] border-t border-[#E3ECE8]">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div data-testid="qualifications-card" className="bg-white rounded-2xl border border-[#D3E8E4] shadow-sm p-6 sm:p-8">
              <h2 data-testid="qualifications-name" className="hero-display text-xl sm:text-2xl font-black text-[#1B365D]">Jo Hobbs</h2>
              <ul data-testid="qualifications-list" className="mt-4 space-y-3">
                {qualifications.map((q) => (
                  <li key={q} className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-[#E4F0EE] border border-[#C6E2DD] flex items-center justify-center shrink-0">
                      <svg className="w-3.5 h-3.5 text-[#2D5A60]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                    <span className="text-sm sm:text-base text-[#355B63] font-medium">{q}</span>
                  </li>
                ))}
              </ul>
              <p data-testid="qualifications-location" className="mt-5 pt-4 border-t border-[#E3ECE8] text-[11px] sm:text-xs text-[#4E757D]">
                Based in the Bundaberg region, QLD • Online counselling available Australia-wide
              </p>
            </div>
          </div>
        </section>

        {/* Closing */}
        <section className="py-14 sm:py-16 bg-[#F4F9F8] border-t border-[#DDECE9]">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
            <div data-testid="about-closing" className="bg-[#FAF6EE] border-l-4 border-[#E5C158] rounded-xl p-5 sm:p-6 space-y-2">
              <p className="text-base sm:text-lg text-[#1B365D] font-bold">
                You don’t have to know exactly what you need before reaching out.
              </p>
              <p className="text-sm sm:text-base text-[#355B63]">
                Sometimes the first step is simply having somewhere safe to start talking.
              </p>
            </div>
            <button
              type="button"
              onClick={talkWithJo}
              data-testid="about-talk-with-jo-btn"
              className="mt-8 inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl text-sm sm:text-base font-extrabold uppercase tracking-wider bg-[#E5C158] hover:bg-[#D4AF37] text-[#1B365D] shadow-md hover:shadow-lg transition-shadow duration-150 transform hover:-translate-y-0.5 border border-[#F1DC9B]"
            >
              Talk With Jo
            </button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

const CounsellingPage = () => {
  const navigate = useNavigate();
  const requestSession = () => {
    navigate("/");
    setTimeout(() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }), 500);
  };

  const supportAreas = [
    "Recovering after a controlling, abusive or emotionally harmful relationship",
    "Anxiety and feeling constantly on edge",
    "Loss of confidence or trust in yourself",
    "Guilt, grief or confusion after leaving a relationship",
    "Rebuilding your identity and sense of self",
    "Boundaries and relationships",
    "Feeling stuck or unsure what comes next",
    "Simply needing somewhere safe to talk",
  ];

  return (
    <div className="min-h-screen bg-[#FAF9F5] text-[#1E373B] antialiased selection:bg-[#E5C158]/30">
      <Header />
      <main>
        {/* Intro */}
        <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-[#FAF9F5] via-[#F2F8F7] to-[#FAF6EE]">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 hero-enter">
            <div
              data-testid="counselling-badge"
              className="inline-flex items-center gap-2 px-3.5 py-1 rounded-md bg-[#E4F0EE] border border-[#C6E2DD] text-xs font-semibold text-[#2D5A60]"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]"></span>
              <span>A safer you is possible</span>
            </div>
            <h1 data-testid="counselling-heading" className="hero-display text-3xl sm:text-5xl font-black text-[#1B365D] leading-[1.1] mt-5">
              Counselling
            </h1>
            <div data-testid="counselling-intro" className="text-base sm:text-lg text-[#355B63] space-y-4 leading-relaxed mt-6">
              <p>You don’t need to have everything figured out before you come to counselling.</p>
              <p>
                Sometimes you simply need somewhere private to talk, make sense of what you’re feeling and work out what comes next.
              </p>
              <p className="text-[#1E373B] font-medium bg-[#EBF5F3]/70 p-4 rounded-xl border-l-4 border-[#3A7D81]">
                Safe Harbor offers individual counselling with Jo Hobbs by Zoom or phone, available to people across Australia.
              </p>
            </div>
          </div>
        </section>

        {/* What you might want support with */}
        <section className="py-14 sm:py-16 bg-[#F4F9F8] border-t border-[#DDECE9]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 data-testid="support-areas-heading" className="hero-display text-2xl sm:text-3xl font-black text-[#1B365D]">
              What you might want support with
            </h2>
            <div data-testid="support-areas-list" className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-8">
              {supportAreas.map((item, i) => (
                <div key={i} className="flex items-start gap-3 bg-white/85 rounded-xl border border-[#D3E8E4] px-4 py-3.5 shadow-sm">
                  <span className="mt-1.5 w-2 h-2 rounded-full bg-[#D4AF37] shrink-0"></span>
                  <p className="text-sm sm:text-base text-[#1E373B] font-medium">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Your first session */}
        <section className="py-14 sm:py-16 bg-[#FAF9F5] border-t border-[#E3ECE8]">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 data-testid="first-session-heading" className="hero-display text-2xl sm:text-3xl font-black text-[#1B365D]">
              Your first session
            </h2>
            <div data-testid="first-session-copy" className="text-base sm:text-lg text-[#355B63] space-y-4 leading-relaxed mt-5">
              <p>Starting counselling can feel daunting, particularly if you’ve never done it before.</p>
              <p>
                Your first session is simply a chance for us to talk. You can tell me what has brought you to counselling
                and what you would like support with — but you don’t have to tell your whole story.
              </p>
              <p>
                There is no test, no expectation that you know exactly what to say and no pressure to talk about anything
                you’re not ready for.
              </p>
              <p className="text-[#1B365D] font-bold">We start where you are.</p>
            </div>
          </div>
        </section>

        {/* Choose what feels comfortable */}
        <section className="py-14 sm:py-16 bg-[#E7F2EF] border-t border-[#DDECE9] relative overflow-hidden">
          <svg className="absolute -bottom-6 right-0 w-full h-28 opacity-25 text-[#3A7D81]" viewBox="0 0 400 150" fill="none" preserveAspectRatio="none">
            <path d="M0 60 C120 120 280 20 400 80 L400 150 L0 150 Z" fill="currentColor" />
            <path d="M0 90 C150 40 260 140 400 100" stroke="#D4AF37" strokeWidth="2" />
          </svg>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <h2 data-testid="comfort-heading" className="hero-display text-2xl sm:text-3xl font-black text-[#1B365D]">
              Choose what feels comfortable
            </h2>
            <div data-testid="comfort-cards" className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 mt-8">
              <div data-testid="zoom-card" className="bg-white/85 rounded-2xl border border-[#D3E8E4] p-5 sm:p-6 shadow-sm">
                <span className="inline-block w-8 h-1 rounded-full bg-[#E5C158] mb-3"></span>
                <h3 className="text-sm font-bold text-[#1B365D] uppercase tracking-wider">Zoom Counselling</h3>
                <p className="text-sm text-[#4E757D] mt-2 leading-relaxed">
                  Talk face-to-face from a private place that feels comfortable for you.
                </p>
              </div>
              <div data-testid="phone-card" className="bg-white/85 rounded-2xl border border-[#D3E8E4] p-5 sm:p-6 shadow-sm">
                <span className="inline-block w-8 h-1 rounded-full bg-[#E5C158] mb-3"></span>
                <h3 className="text-sm font-bold text-[#1B365D] uppercase tracking-wider">Phone Counselling</h3>
                <p className="text-sm text-[#4E757D] mt-2 leading-relaxed">
                  No camera required. Sometimes talking without being seen can make taking that first step easier.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Session information / pricing */}
        <section className="py-14 sm:py-16 bg-[#FAF9F5] border-t border-[#E3ECE8]">
          <div className="max-w-md mx-auto px-4 sm:px-6">
            <h2 data-testid="session-info-heading" className="hero-display text-2xl sm:text-3xl font-black text-[#1B365D] text-center">
              Session information
            </h2>
            <div data-testid="pricing-card" className="mt-8 bg-white rounded-2xl border border-[#D3E8E4] shadow-md overflow-hidden">
              <div className="h-1.5 bg-gradient-to-r from-[#3A7D81] via-[#E5C158] to-[#3A7D81]"></div>
              <div className="p-6 sm:p-8 text-center">
                <p className="text-xs font-bold uppercase tracking-wider text-[#2D5A60]">60-minute counselling session</p>
                <p data-testid="pricing-amount" className="hero-display text-5xl font-black text-[#1B365D] mt-3">$110</p>
                <div className="mt-5 space-y-2 text-sm text-[#355B63]">
                  <p className="flex items-center justify-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]"></span> Zoom or phone
                  </p>
                  <p className="flex items-center justify-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]"></span> Australia-wide
                  </p>
                </div>
                <div className="mt-6 pt-5 border-t border-[#E3ECE8]">
                  <p data-testid="concession-rate" className="text-sm font-bold text-[#1B365D]">Concession rate: $90</p>
                  <p className="text-[11px] text-[#4E757D] mt-1">Limited concession places available.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Closing */}
        <section className="py-14 sm:py-16 bg-[#F4F9F8] border-t border-[#DDECE9]">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
            <div data-testid="counselling-closing" className="bg-[#FAF6EE] border-l-4 border-[#E5C158] rounded-xl p-5 sm:p-6 space-y-2">
              <p className="text-base sm:text-lg text-[#1B365D] font-bold">
                You don’t have to decide what your whole recovery looks like today.
              </p>
              <p className="text-sm sm:text-base text-[#355B63]">
                You can simply start with one conversation.
              </p>
            </div>
            <button
              type="button"
              onClick={requestSession}
              data-testid="request-session-btn"
              className="mt-8 inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl text-sm sm:text-base font-extrabold uppercase tracking-wider bg-[#E5C158] hover:bg-[#D4AF37] text-[#1B365D] shadow-md hover:shadow-lg transition-shadow duration-150 transform hover:-translate-y-0.5 border border-[#F1DC9B]"
            >
              Request a Session
            </button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

const FaqPage = () => {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState(null);
  const contactJo = () => {
    navigate("/");
    setTimeout(() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }), 500);
  };

  const faqs = [
    {
      q: "Do I need a referral?",
      a: ["No. You can contact Safe Harbor directly and request an appointment. You do not need a referral to access counselling."],
    },
    {
      q: "How much does counselling cost?",
      a: ["A 60-minute counselling session is $110.", "A limited number of concession appointments are available at $90."],
    },
    {
      q: "How are sessions held?",
      a: ["Counselling is available by Zoom or phone, so you can choose whichever feels most comfortable for you."],
    },
    {
      q: "Where is counselling available?",
      a: ["Safe Harbor Supports You is based in the Bundaberg region, Queensland, with Zoom and phone counselling available Australia-wide."],
    },
    {
      q: "What happens during my first session?",
      a: [
        "Your first session is a chance for us to talk about what has brought you to counselling and what you would like support with.",
        "You don’t need to prepare anything, know exactly what to say or tell your whole story. We start wherever you are.",
      ],
    },
    {
      q: "What if I find it difficult to talk?",
      a: [
        "That’s okay. There is no expectation that you will immediately feel comfortable talking about difficult experiences.",
        "We can take things slowly, and you decide what you are ready to talk about.",
      ],
    },
    {
      q: "Is counselling confidential?",
      a: [
        "Your privacy is important. What you discuss in counselling is treated confidentially.",
        "There are some circumstances where confidentiality may need to be broken, including where there is a serious risk of harm or where disclosure is required by law. These limits will be explained to you as part of beginning counselling.",
      ],
    },
    {
      q: "Do you only counsel people who have experienced abusive relationships?",
      a: ["No. Safe Harbor has a particular interest in supporting people recovering from controlling, abusive or emotionally harmful relationships, but counselling is also available for people experiencing anxiety, grief, relationship difficulties, loss of confidence, life changes or simply needing somewhere supportive to talk."],
    },
    {
      q: "Can I have phone counselling if I don’t want to be on camera?",
      a: ["Absolutely. You do not have to use Zoom or appear on camera. Phone counselling is available if that feels more comfortable."],
    },
    {
      q: "How do I book?",
      a: [
        "Use the BOOK SESSION or REQUEST A SESSION button anywhere on the website. This will take you to the appointment request form.",
        "Once your request is received, Jo will contact you to arrange your appointment.",
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#FAF9F5] text-[#1E373B] antialiased selection:bg-[#E5C158]/30">
      <Header />
      <main>
        <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-[#FAF9F5] via-[#F2F8F7] to-[#FAF6EE]">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 hero-enter">
            <div
              data-testid="faq-badge"
              className="inline-flex items-center gap-2 px-3.5 py-1 rounded-md bg-[#E4F0EE] border border-[#C6E2DD] text-xs font-semibold text-[#2D5A60] uppercase tracking-wider"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]"></span>
              <span>Questions before you begin</span>
            </div>
            <h1 data-testid="faq-heading" className="hero-display text-3xl sm:text-5xl font-black text-[#1B365D] leading-[1.1] mt-5">
              Frequently Asked Questions
            </h1>
            <p data-testid="faq-intro" className="text-base sm:text-lg text-[#355B63] leading-relaxed mt-6">
              It’s completely okay to have questions before starting counselling. Here are some of the things you might
              want to know before reaching out.
            </p>
          </div>
        </section>

        <section className="py-14 sm:py-16 bg-[#F4F9F8] border-t border-[#DDECE9]">
          <div className="max-w-2xl mx-auto px-4 sm:px-6">
            <div data-testid="faq-accordion" className="space-y-3">
              {faqs.map((item, i) => {
                const open = openIndex === i;
                return (
                  <div key={i} data-testid={`faq-item-${i}`} className="bg-white rounded-xl border border-[#D3E8E4] shadow-sm overflow-hidden">
                    <button
                      type="button"
                      data-testid={`faq-question-${i}`}
                      onClick={() => setOpenIndex(open ? null : i)}
                      aria-expanded={open}
                      className="w-full flex items-center justify-between gap-4 px-4 sm:px-5 py-4 text-left"
                    >
                      <span className="text-sm sm:text-base font-bold text-[#1B365D]">{item.q}</span>
                      <svg
                        className={`w-4 h-4 shrink-0 text-[#3A7D81] transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                        fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    <div className={`grid transition-[grid-template-rows] duration-200 ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                      <div className="overflow-hidden">
                        <div data-testid={`faq-answer-${i}`} className="px-4 sm:px-5 pb-4 space-y-2 border-t border-[#E3ECE8] pt-3">
                          {item.a.map((p, j) => (
                            <p key={j} className="text-sm text-[#4E757D] leading-relaxed">{p}</p>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-14 sm:py-16 bg-[#FAF9F5] border-t border-[#E3ECE8]">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
            <h2 data-testid="faq-closing-heading" className="hero-display text-2xl sm:text-3xl font-black text-[#1B365D]">
              Still have a question?
            </h2>
            <p data-testid="faq-closing-text" className="text-sm sm:text-base text-[#355B63] leading-relaxed mt-4">
              You don’t need to be certain that counselling is right for you before reaching out. You’re welcome to ask a
              question first.
            </p>
            <button
              type="button"
              onClick={contactJo}
              data-testid="contact-jo-btn"
              className="mt-8 inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl text-sm sm:text-base font-extrabold uppercase tracking-wider bg-[#E5C158] hover:bg-[#D4AF37] text-[#1B365D] shadow-md hover:shadow-lg transition-shadow duration-150 transform hover:-translate-y-0.5 border border-[#F1DC9B]"
            >
              Contact Jo
            </button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/counselling" element={<CounsellingPage />} />
          <Route path="/faq" element={<FaqPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
